/**
 * Складской учёт — API-клиент.
 *
 * Доступ ограничен на бэке: admin / head_op / storekeeper / superuser.
 */
import { api } from "./client";


export type WarehouseName = "СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС" | string;
export type Direction = "outflow" | "inflow";


/* ── Stocks ──────────────────────────────────────────────────── */
export interface StockRow {
  id: number;
  item_id: number;
  warehouse: string;
  quantity: number;
  reserved: number;
  critical_qty: number | null;
  price_buy: number | null;
  price_sell: number | null;
  note: string | null;
  snapshot_at: string | null;
  updated_at: string;
  sku: string | null;
  name: string | null;
  unit: string | null;
  section: string | null;
}

export interface StocksListResponse {
  items: StockRow[];
  count: number;
}

export interface StocksSummary {
  positions: number;
  in_stock: number;
  zero_stock: number;
  low_stock: number;
  value_buy: number;
  value_sell: number;
  total_reserved: number;
  by_warehouse: { warehouse: string; positions: number; quantity: number; value_buy: number }[];
}


/* ── Items / Contractors / Employees ─────────────────────────── */
export interface ItemRow {
  id: number;
  sku: string | null;
  name: string;
  unit: string | null;
  section: string | null;
  warehouse: string | null;
  price_buy: number | null;
  price_sell: number | null;
  note: string | null;
  archived: boolean;
}

export interface ItemsListResponse {
  total: number;
  items: ItemRow[];
}

export interface ContractorRow {
  id: number;
  name: string;
  kind: "supplier" | "customer" | "both";
  customer_kind: "individual" | "on_object" | "self_pickup" | null;
  default_lead_days: number | null;
  address: string | null;
  phone: string | null;
  note: string | null;
}

export interface EmployeeRow {
  id: number;
  full_name: string;
  position: string | null;
  linked_user_id: string | null;
  archived: boolean;
}


/* ── Inflow / Outflow docs ───────────────────────────────────── */
export interface DocRow {
  id: number;
  doc_number: string | null;
  doc_date: string | null;
  warehouse: string | null;
  contractor_id: number | null;
  contractor_name: string | null;
  employee_id: number | null;
  employee_name: string | null;
  note: string | null;
  total_amount: number | null;
  deal_amo_id: number | null;
  object_id: number | null;
  created_at: string;
  items_count: number;
  items_amount: number;
}

export interface DocsListResponse {
  total: number;
  items: DocRow[];
  sum_amount: number;
}

export interface DocItem {
  id: number;
  doc_id: number;
  line_no: number | null;
  item_id: number | null;
  name: string;
  sku: string | null;
  unit: string | null;
  quantity: number;
  price_buy?: number | null;
  price_sell?: number | null;
  price?: number | null;
  price_buy_at_sale?: number | null;
  amount: number | null;
}


/* ── Overview ────────────────────────────────────────────────── */
export interface TurnoverBlock {
  docs: number;
  quantity: number;
  amount: number;
  amount_buy?: number;
  cogs?: number;
  margin?: number;
  margin_pct?: number | null;
}

export interface MonthRow {
  month: string;
  inflow: number;
  outflow: number;
  cogs: number;
  margin: number;
}

export interface TopItem {
  name: string;
  sku: string | null;
  unit: string | null;
  rows_count: number;
  quantity: number;
  amount: number;
  share_pct: number;
  cumulative_pct: number;
  abc: "A" | "B" | "C";
}

export interface TopContractor {
  name: string;
  docs: number;
  quantity: number;
  amount: number;
}

export interface WarehouseOverviewResponse {
  period: { from: string; to: string };
  warehouse: string | null;
  stocks: StocksSummary;
  turnover: {
    period: { from: string; to: string };
    inflow: TurnoverBlock;
    outflow: TurnoverBlock;
  };
  by_month: MonthRow[];
  top_outflow_items: TopItem[];
  top_inflow_items: TopItem[];
  top_suppliers: TopContractor[];
  top_customers: TopContractor[];
}


/* ── Helpers ─────────────────────────────────────────────────── */
function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}


/* ── Endpoints ───────────────────────────────────────────────── */
export const fetchWarehouseOverview = (params: { date_from: string; date_to: string; warehouse?: string }): Promise<WarehouseOverviewResponse> =>
  api(`/api/warehouse/overview${qs(params)}`);

export const fetchStocks = (params: { warehouse?: string; low_only?: boolean; search?: string; limit?: number; offset?: number } = {}): Promise<StocksListResponse> =>
  api(`/api/warehouse/stocks${qs(params)}`);

export const fetchStocksSummary = (params: { warehouse?: string } = {}): Promise<StocksSummary> =>
  api(`/api/warehouse/stocks/summary${qs(params)}`);

export interface StockPatch {
  reserved?: number | null;
  critical_qty?: number | null;
  price_buy?: number | null;
  price_sell?: number | null;
  note?: string | null;
  quantity?: number | null;
}

export const patchStock = (stockId: number, payload: StockPatch): Promise<StockRow> =>
  api(`/api/warehouse/stocks/${stockId}`, { method: "PATCH", body: JSON.stringify(payload) });

export interface ItemPatch {
  sku?: string | null;
  name?: string | null;
  unit?: string | null;
  section?: string | null;
  price_buy?: number | null;
  price_sell?: number | null;
  note?: string | null;
  expires_at?: string | null;     // F10
}

export const patchItem = (itemId: number, payload: ItemPatch): Promise<ItemRow> =>
  api(`/api/warehouse/items/${itemId}`, { method: "PATCH", body: JSON.stringify(payload) });

export interface ItemCreate {
  name: string;
  sku?: string | null;
  unit?: string | null;
  section?: string | null;
  warehouse?: string | null;
  price_buy?: number | null;
  price_sell?: number | null;
  note?: string | null;
  expires_at?: string | null;     // F10
}

export const createItem = (payload: ItemCreate): Promise<ItemRow> =>
  api(`/api/warehouse/items`, { method: "POST", body: JSON.stringify(payload) });


/* ── Audit (журнал изменений номенклатуры) ──────────────────────────── */
export interface ItemAuditRow {
  id: number;
  item_id: number;
  user_id: string | null;
  user_name: string | null;
  action: "create" | "update";
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_at: string;
  item_sku: string | null;
  item_name: string | null;
  item_section: string | null;
}
export interface ItemsAuditResponse {
  items: ItemAuditRow[];
  total: number;
}
export const fetchItemsAudit = (params: { item_id?: number; limit?: number; offset?: number } = {}): Promise<ItemsAuditResponse> =>
  api(`/api/warehouse/items/audit${qs(params)}`);

export const fetchUnits = (): Promise<string[]> =>
  api(`/api/warehouse/units`);

export const fetchWarehouses = (): Promise<string[]> =>
  api(`/api/warehouse/warehouses`);

export const fetchItems = (params: { warehouse?: string; section?: string; search?: string; limit?: number; offset?: number } = {}): Promise<ItemsListResponse> =>
  api(`/api/warehouse/items${qs(params)}`);

export const fetchSections = (): Promise<string[]> =>
  api(`/api/warehouse/items/sections`);

export const fetchContractors = (params: { kind?: "supplier" | "customer" | "both" } = {}): Promise<ContractorRow[]> =>
  api(`/api/warehouse/contractors${qs(params)}`);

/** Подтип заказчика — из оригинального справочника:
 *  individual = «Физ.Лицо», on_object = «На Объект», self_pickup = «Физ.Лицо (самовывоз)». */
export type CustomerKind = "individual" | "on_object" | "self_pickup";
export const CUSTOMER_KIND_LABEL: Record<CustomerKind, string> = {
  individual: "Физ. лицо",
  on_object:  "На объект",
  self_pickup:"Самовывоз",
};

export const fetchEmployees = (): Promise<EmployeeRow[]> =>
  api(`/api/warehouse/employees`);

export const fetchInflows = (params: { date_from: string; date_to: string; warehouse?: string; contractor_id?: number; employee_id?: number; search?: string; limit?: number; offset?: number }): Promise<DocsListResponse> =>
  api(`/api/warehouse/inflows${qs(params)}`);

export const fetchInflow = (id: number): Promise<{ doc: DocRow; items: DocItem[] }> =>
  api(`/api/warehouse/inflows/${id}`);

export const fetchOutflows = (params: { date_from: string; date_to: string; warehouse?: string; contractor_id?: number; employee_id?: number; search?: string; limit?: number; offset?: number }): Promise<DocsListResponse> =>
  api(`/api/warehouse/outflows${qs(params)}`);

export const fetchOutflow = (id: number): Promise<{ doc: DocRow; items: DocItem[] }> =>
  api(`/api/warehouse/outflows/${id}`);

export const fetchTopItems = (params: { date_from: string; date_to: string; direction?: Direction; warehouse?: string; limit?: number }): Promise<TopItem[]> =>
  api(`/api/warehouse/top/items${qs(params)}`);

export const fetchTopContractors = (params: { date_from: string; date_to: string; direction?: Direction; limit?: number }): Promise<TopContractor[]> =>
  api(`/api/warehouse/top/contractors${qs(params)}`);

export const fetchTopEmployees = (params: { date_from: string; date_to: string; direction?: Direction; limit?: number }): Promise<TopContractor[]> =>
  api(`/api/warehouse/top/employees${qs(params)}`);


/* ── Blanks (документы «Приложения») ─────────────────────────── */
export interface BlankFile {
  key: string;
  name: string;
  category: string;
  category_path: string;
  size_bytes: number;
  modified: string;
  ext: string;
}

export interface BlanksListResponse {
  items: BlankFile[];
  root_missing: boolean;
  root: string;
}

export const fetchBlanks = (): Promise<BlanksListResponse> =>
  api(`/api/warehouse/blanks`);

export function blankDownloadUrl(key: string, decrypted = true): string {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const u = new URLSearchParams({ key, decrypted: String(decrypted) });
  return `${base}/warehouse/blanks/download?${u.toString()}`;
}


/* ── Save cart as doc ─────────────────────────────────────── */
export interface CartSaveLine {
  item_id: number;
  quantity: number;
  price?: number | null;
  note?: string | null;
}

export interface CartSavePayload {
  direction: "inflow" | "outflow";
  warehouse?: string | null;
  doc_number?: string | null;
  doc_date?: string | null;
  contractor_name?: string | null;
  note?: string | null;
  items: CartSaveLine[];
  /** Привязка к объекту (для outflow «На Объект») — связка с production_objects. */
  object_id?: number | null;
  deal_amo_id?: number | null;
}


/* ── Объекты (production_objects) — для select'а в форме отгрузки ──── */
export interface ObjectForSelect {
  id: number;
  deal_amo_id: number | null;
  client_name: string;
  region: string | null;
  sqm: number | null;
  status: string;
  planned_start: string | null;
  planned_end: string | null;
  address: string | null;
}
export const fetchObjects = (params: { status?: string; search?: string } = {}): Promise<ObjectForSelect[]> =>
  api(`/api/warehouse/objects${qs(params)}`);

/** Удобный label для отображения объекта в выпадушке. */
export function formatObjectLabel(o: ObjectForSelect): string {
  const parts: string[] = [o.client_name || `#${o.id}`];
  if (o.region) parts.push(o.region);
  if (o.sqm != null) parts.push(`${o.sqm} м²`);
  return parts.join(" · ");
}

export interface DocCounters { inflow: number; outflow: number; }
export const fetchDocCounters = (): Promise<DocCounters> =>
  api(`/api/warehouse/counters`);

export const saveCartAsDoc = (payload: CartSavePayload): Promise<{ id: number; direction: string; doc_number: string; items_count: number }> =>
  api(`/api/warehouse/docs`, { method: "POST", body: JSON.stringify(payload) });


/* ── UI helpers ──────────────────────────────────────────────── */
export function formatRub(v: number, opts: { unit?: boolean; mln?: boolean } = {}): string {
  const { unit = true, mln = true } = opts;
  if (mln && Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(".", ",") + (unit ? " млн ₽" : "");
  if (mln && Math.abs(v) >= 100_000) return Math.round(v / 1000).toLocaleString("ru-RU") + (unit ? " тыс ₽" : "");
  return Math.round(v).toLocaleString("ru-RU") + (unit ? " ₽" : "");
}

export function formatQty(v: number, unit?: string | null): string {
  const n = Math.abs(v) >= 100 ? Math.round(v) : v.toFixed(2).replace(/\.?0+$/, "");
  return `${n}${unit ? " " + unit : ""}`;
}

export function monthLabel(s: string): string {
  const months = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];
  const [y, m] = s.split("-").map(Number);
  if (!y || !m) return s;
  return `${months[m - 1]} ${y}`;
}

export const ABC_COLORS: Record<"A" | "B" | "C", string> = {
  A: "#3D7A3D",
  B: "#C97A1F",
  C: "#8A7F77",
};


/* ── BOM / Наборы (Phase 3) ──────────────────────────────────────────── */
export interface KitRow {
  id: number;
  sku: string | null;
  name: string;
  sqm: number | null;
  description: string | null;
  archived: boolean;
  items_count: number;
}

export interface KitItemRow {
  id: number;
  kit_id: number;
  item_id: number;
  quantity: number;
  is_optional: boolean;
  note: string | null;
  item_name: string | null;
  item_sku: string | null;
  item_unit: string | null;
  item_section: string | null;
}

export interface KitPayload {
  name: string;
  sku?: string | null;
  sqm?: number | null;
  description?: string | null;
}

export interface KitItemPayload {
  item_id: number;
  quantity: number;
  is_optional?: boolean;
  note?: string | null;
}

export const fetchKits = (): Promise<KitRow[]> =>
  api(`/api/warehouse/kits`);

export const fetchKitItems = (kitId: number): Promise<KitItemRow[]> =>
  api(`/api/warehouse/kits/${kitId}/items`);

export const createKit = (payload: KitPayload): Promise<{ id: number }> =>
  api(`/api/warehouse/kits`, { method: "POST", body: JSON.stringify(payload) });

export const updateKit = (kitId: number, payload: KitPayload): Promise<{ id: number }> =>
  api(`/api/warehouse/kits/${kitId}`, { method: "PATCH", body: JSON.stringify(payload) });

export const archiveKit = (kitId: number): Promise<void> =>
  api(`/api/warehouse/kits/${kitId}`, { method: "DELETE" });

export const addKitItem = (kitId: number, payload: KitItemPayload): Promise<{ id: number; kit_id: number; item_id: number }> =>
  api(`/api/warehouse/kits/${kitId}/items`, { method: "POST", body: JSON.stringify(payload) });

export const removeKitItem = (kitId: number, itemId: number): Promise<void> =>
  api(`/api/warehouse/kits/${kitId}/items/${itemId}`, { method: "DELETE" });

export const setObjectKit = (objectId: number, kitId: number | null): Promise<{ object_id: number; kit_id: number | null }> =>
  api(`/api/warehouse/objects/${objectId}/kit`, { method: "POST", body: JSON.stringify({ kit_id: kitId }) });


/* ── Planning (Phase 3) — главная страница «что дозаказать» ─────────── */
export interface PlanningProject {
  id: number;
  deal_amo_id: number | null;
  client_name: string;
  region: string | null;
  sqm: number | null;
  status: string;
  planned_start: string | null;
  planned_end: string | null;
  kit_id: number | null;
  kit_name: string | null;
  kit_sku: string | null;
}

export interface PlanningRow {
  item_id: number;
  name: string;
  sku: string | null;
  unit: string | null;
  section: string | null;
  required: number;
  on_hand: number;
  reserved: number;
  on_order: number;        // Phase 2: уже заказано у поставщика
  available: number;
  need_to_order: number;
  price_buy: number;
  estimated_cost: number | null;
}

export interface PlanningResponse {
  required: PlanningRow[];
  projects: {
    with_kit: PlanningProject[];
    without_kit: PlanningProject[];
  };
  summary: {
    items: number;
    need_to_order_items: number;
    total_need_cost?: number;
  };
}

export const fetchPlanning = (): Promise<PlanningResponse> =>
  api(`/api/warehouse/planning`);


/* ── Phase 2: Заказы поставщикам (PO) ──────────────────────────────── */
export type POStatus = "draft" | "sent" | "partial" | "received" | "cancelled";

export const PO_STATUS_LABEL: Record<POStatus, string> = {
  draft:     "Черновик",
  sent:      "Отправлен",
  partial:   "Частично",
  received:  "Получен",
  cancelled: "Отменён",
};

export interface PORow {
  id: number;
  doc_number: string | null;
  status: POStatus;
  supplier_id: number | null;
  supplier_name: string | null;
  order_date: string;
  expected_date: string | null;
  warehouse: string | null;
  employee_name: string | null;
  total_amount: number;
  note: string | null;
  items_count: number;
  pending_qty: number;
}

export interface POItemRow {
  id: number;
  order_id: number;
  item_id: number;
  sku: string | null;
  name: string;
  unit: string | null;
  item_section: string | null;
  quantity_ordered: number;
  quantity_received: number;
  price: number | null;
  amount: number | null;
}

export interface POListResponse {
  items: PORow[];
  total: number;
}

export interface POCreatePayload {
  doc_number?: string | null;
  supplier_id?: number | null;
  supplier_name?: string | null;
  order_date?: string | null;
  expected_date?: string | null;
  warehouse?: string | null;
  note?: string | null;
  items: { item_id: number; quantity: number; price?: number | null }[];
}

export interface POReceiveLine {
  item_id: number;
  quantity_received: number;
  price?: number | null;
}

export const fetchPurchaseOrders = (params: { status?: string; supplier_id?: number; limit?: number; offset?: number } = {}): Promise<POListResponse> =>
  api(`/api/warehouse/purchase-orders${qs(params)}`);

export const fetchPurchaseOrder = (orderId: number): Promise<{ doc: PORow; items: POItemRow[] }> =>
  api(`/api/warehouse/purchase-orders/${orderId}`);

export const createPurchaseOrder = (payload: POCreatePayload): Promise<{ id: number; items_count: number; total_amount: number }> =>
  api(`/api/warehouse/purchase-orders`, { method: "POST", body: JSON.stringify(payload) });

export const updatePOStatus = (orderId: number, status: POStatus): Promise<{ id: number; status: POStatus }> =>
  api(`/api/warehouse/purchase-orders/${orderId}/status`, { method: "PATCH", body: JSON.stringify({ status }) });

export const cancelPurchaseOrder = (orderId: number): Promise<void> =>
  api(`/api/warehouse/purchase-orders/${orderId}`, { method: "DELETE" });

export const receivePurchaseOrder = (orderId: number, lines: POReceiveLine[], createInflow = true): Promise<{ order_id: number; status: POStatus; inflow_doc_id: number | null; received_total: number }> =>
  api(`/api/warehouse/purchase-orders/${orderId}/receive`, {
    method: "POST", body: JSON.stringify({ lines, create_inflow: createInflow }),
  });


/* ── Phase 2.5: Заказы клиентов / SO (резервы под проект) ────────────── */
export type SOStatus = "draft" | "confirmed" | "partial" | "shipped" | "cancelled";

export const SO_STATUS_LABEL: Record<SOStatus, string> = {
  draft:     "Черновик",
  confirmed: "Подтверждён",
  partial:   "Частично отгружен",
  shipped:   "Отгружен",
  cancelled: "Отменён",
};

export interface SORow {
  id: number;
  doc_number: string | null;
  status: SOStatus;
  customer_id: number | null;
  customer_name: string | null;
  object_id: number | null;
  deal_amo_id: number | null;
  object_client_name: string | null;
  order_date: string;
  ship_by: string | null;
  address: string | null;
  phone: string | null;
  payment_method: string | null;
  warehouse: string | null;
  employee_name: string | null;
  total_amount: number;
  note: string | null;
  items_count: number;
  pending_qty: number;
}

export interface SOItemRow {
  id: number;
  order_id: number;
  item_id: number;
  sku: string | null;
  name: string;
  unit: string | null;
  item_section: string | null;
  quantity_ordered: number;
  quantity_shipped: number;
  price: number | null;
  amount: number | null;
  note: string | null;
}

export interface SOListResponse {
  items: SORow[];
  total: number;
}

export interface SOCreatePayload {
  doc_number?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  object_id?: number | null;
  deal_amo_id?: number | null;
  order_date?: string | null;
  ship_by?: string | null;
  address?: string | null;
  phone?: string | null;
  payment_method?: string | null;
  warehouse?: string | null;
  note?: string | null;
  items: { item_id: number; quantity: number; price?: number | null; note?: string | null }[];
}

export interface SOShipLine {
  item_id: number;
  quantity_shipped: number;
  price?: number | null;
}

export const fetchSalesOrders = (params: { status?: string; object_id?: number; limit?: number; offset?: number } = {}): Promise<SOListResponse> =>
  api(`/api/warehouse/sales-orders${qs(params)}`);

export const fetchSalesOrder = (orderId: number): Promise<{ doc: SORow; items: SOItemRow[] }> =>
  api(`/api/warehouse/sales-orders/${orderId}`);

export const createSalesOrder = (payload: SOCreatePayload): Promise<{ id: number; items_count: number; total_amount: number }> =>
  api(`/api/warehouse/sales-orders`, { method: "POST", body: JSON.stringify(payload) });

export const updateSOStatus = (orderId: number, status: SOStatus): Promise<{ id: number; status: SOStatus }> =>
  api(`/api/warehouse/sales-orders/${orderId}/status`, { method: "PATCH", body: JSON.stringify({ status }) });

export const cancelSalesOrder = (orderId: number): Promise<void> =>
  api(`/api/warehouse/sales-orders/${orderId}`, { method: "DELETE" });

export const shipSalesOrder = (orderId: number, lines: SOShipLine[], createOutflow = true): Promise<{ order_id: number; status: SOStatus; outflow_doc_id: number | null; shipped_total: number }> =>
  api(`/api/warehouse/sales-orders/${orderId}/ship`, {
    method: "POST", body: JSON.stringify({ lines, create_outflow: createOutflow }),
  });


/* ── Phase 4: Карточка позиции + expires_at (F10) ────────────────── */
export interface ItemCard {
  item: {
    id: number;
    sku: string | null;
    name: string;
    unit: string | null;
    section: string | null;
    warehouse: string | null;
    price_buy: number | null;
    price_sell: number | null;
    note: string | null;
    expires_at: string | null;
    archived: boolean;
    created_at: string | null;
    updated_at: string | null;
  };
  stocks: Array<{
    warehouse: string;
    quantity: number;
    reserved: number;
    critical_qty: number | null;
    snapshot_at: string | null;
    updated_at: string | null;
  }>;
  movements: Array<{
    date: string;
    direction: "in" | "out";
    quantity: number;
    amount: number;
    counterparty: string | null;
  }>;
  by_month: Array<{ month: string; inflow: number; outflow: number }>;
  top_suppliers: Array<{ name: string; docs: number; qty: number; amount: number }>;
  top_customers: Array<{ name: string; docs: number; qty: number; amount: number }>;
  on_order: number;
  reserved_so: number;
}

export const fetchItemCard = (itemId: number, days = 90): Promise<ItemCard> =>
  api(`/api/warehouse/items/${itemId}/card${qs({ days })}`);


/* ── F8: Гранулярные права ────────────────────────────────────────── */
export type WarehousePermission =
  | "items.edit"
  | "inflow.create" | "outflow.create"
  | "po.create" | "po.receive"
  | "so.create" | "so.edit" | "so.ship"
  | "stocktake.create" | "stocktake.edit" | "stocktake.apply"
  | "kits.edit";

export interface PermissionsResponse {
  all: boolean;
  perms: WarehousePermission[];
  role: string;
}

export const fetchPermissions = (): Promise<PermissionsResponse> =>
  api(`/api/warehouse/permissions`);
